import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  Grid,
} from "@mui/material";
import { CopyAll } from "@mui/icons-material";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import ButtonOne from "../../common/normal-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { GENERATE_QR_GET_NUMBER_ENDPOINT } from "../../config/urls";

// Phone validation libs (same as QR/UserFormDialog)
import { getCountryCallingCode } from "react-phone-number-input/input";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import parsePhoneNumber from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import CountryCodeSelect from "../../common/country-code-select";
import { getCountryForCallingCode as getCountryFromCallingCode } from "../../common/country-codes";

const WAChatLink = () => {
  const { getValidToken } = useAuth();

  const [formState, setFormState] = useState({
    country_iso: "IN",
    phoneNumber: "",
    message: "Hi",
  });

  const [generatedScript, setGeneratedScript] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState("");
  const [messageError, setMessageError] = useState("");

  // 🔹 Get expected digits dynamically
  const getExpectedDigitCount = (isoCode) => {
    try {
      const example = getExampleNumber(isoCode, examples);
      return example?.nationalNumber?.length || null;
    } catch {
      return null;
    }
  };

  // 🔹 Phone validation (same pattern as QR form)
  const validatePhoneNumber = (phone, country_iso) => {
    if (!phone) return "Please enter a phone number";

    const expectedDigits = getExpectedDigitCount(country_iso);

    if (expectedDigits && phone.length !== expectedDigits) {
      return `Enter a valid ${country_iso} phone number (${expectedDigits} digits)`;
    }

    const fullNumber = `+${getCountryCallingCode(country_iso)}${phone}`;

    if (!isPossiblePhoneNumber(fullNumber)) {
      return "Invalid phone number";
    }

    return "";
  };

  // 🔹 Message validation (keep your rule)
  const validateMessage = (text) => {
    const regex = /^[a-zA-Z0-9 +.,!?':;"()/\-[\]]*$/;
    return regex.test(text);
  };

  const handleGenerateLink = () => {
    const phoneError = validatePhoneNumber(
      formState.phoneNumber,
      formState.country_iso
    );

    if (phoneError) {
      setError(phoneError);
      return;
    }

    if (!formState.message.trim()) {
      setMessageError("Message is required");
      return;
    }

    if (formState.message.length > 30) {
      setMessageError("Message cannot exceed 30 characters");
      return;
    }

    if (!validateMessage(formState.message)) {
      setMessageError("Invalid characters in message");
      return;
    }

    setError("");
    setMessageError("");

    const dialCode = getCountryCallingCode(formState.country_iso);
    const phone = formState.phoneNumber.replace(/\D/g, "");
    const msg = encodeURIComponent(formState.message.trim());

    const url = `https://api.whatsapp.com/send/?phone=${dialCode}${phone}&text=${msg}`;

    setGeneratedScript(url);
    setShowAlert(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedScript);
  };

  useEffect(() => {
    if (showAlert) {
      const t = setTimeout(() => setShowAlert(false), 4000);
      return () => clearTimeout(t);
    }
  }, [showAlert]);

  useEffect(() => {
    const fetchBusinessNumber = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const response = await fetch(GENERATE_QR_GET_NUMBER_ENDPOINT, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const rawFullNumber = data.business_wa_number;

          if (rawFullNumber) {
            const formatted = rawFullNumber.startsWith("+")
              ? rawFullNumber
              : `+${rawFullNumber}`;
            const parsed = parsePhoneNumber(formatted);

            if (parsed) {
              setFormState((prev) => ({
                ...prev,
                country_iso: parsed.country || getCountryFromCallingCode(parsed.countryCallingCode) || "IN",
                phoneNumber: parsed.nationalNumber,
                message: "Hi",
              }));
            } else {
              setFormState((prev) => ({
                ...prev,
                phoneNumber: rawFullNumber.replace(/\D/g, ""),
                message: "Hi",
              }));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };
    fetchBusinessNumber();
  }, [getValidToken]);

  return (
    <Card sx={{ maxWidth: 1100, margin: "auto", mt: 2, p: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2.5 }}>
          Generate WhatsApp Chat Link
        </Typography>

        <Grid container spacing={2}>
          {/* Country */}
          <Grid item xs={4}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                mb: 0.5,
                ml: 0.5,
              }}
            >
              Country Code*
            </Typography>

            <CountryCodeSelect
              value={formState.country_iso}
              onChange={(nextIso) => {
                setFormState({ ...formState, country_iso: nextIso });
                setError(
                  validatePhoneNumber(formState.phoneNumber, nextIso),
                );
              }}
              label=""
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={8}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                mb: 0.5,
                ml: 0.5,
              }}
            >
              Phone number*
            </Typography>

            <TextField
              fullWidth
              placeholder="9999999999"
              value={formState.phoneNumber}
              onChange={(e) => {
                const expectedDigits = getExpectedDigitCount(
                  formState.country_iso,
                );
                const raw = e.target.value.replace(/\D/g, "");
                if (expectedDigits && raw.length > expectedDigits) {
                  return;
                }
                setFormState({ ...formState, phoneNumber: raw });
                setError(
                  validatePhoneNumber(raw, formState.country_iso),
                );
              }}
              error={!!error}
              helperText={error}
            />
          </Grid>

          {/* Message */}
          <Grid item xs={12}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                mb: 0.5,
                ml: 0.5,
              }}
            >
              Pre-filled message
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              value={formState.message}
              onChange={(e) => {
                const val = e.target.value;
                setFormState({ ...formState, message: val });

                if (!validateMessage(val)) {
                  setMessageError("Invalid characters");
                } else {
                  setMessageError("");
                }
              }}
              inputProps={{ maxLength: 30 }}
              helperText={
                messageError || `${formState.message.length}/30`
              }
              error={!!messageError}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <ButtonOne
            variant="contained"
            onClick={handleGenerateLink}
            disabled={!formState.message.trim() || !!error}
          >
            Generate Link
          </ButtonOne>
        </Box>

        {showAlert && (
          <Alert sx={{ mt: 2 }} severity="success">
            Link generated successfully!
          </Alert>
        )}

        {generatedScript && (
          <Box sx={{ mt: 2, position: "relative" }}>
            <SyntaxHighlighter
              language="html"
              style={oneLight}
            >
              {generatedScript}
            </SyntaxHighlighter>

            <IconButton
              onClick={handleCopy}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                color: "white",
                backgroundColor: "#25d366",
              }}
            >
              <CopyAll />
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WAChatLink;